import { useCallback } from "react";
import { Button } from "@components/ui/button";
import FullResetButton from "./FullResetButton";
import { useCancelDeploy } from "@api/deploys.hooks";
import { useRedeploy } from "@hooks/useRedeploy";
import { useHasDeployChanges } from "@hooks/useHasDeployChanges";
import type { DeployScreen } from "@hooks/useDeployContentScreen";

const TERMINAL_SCREENS: DeployScreen[] = [
  "deploy-complete",
  "deploy-error",
  "deploy-cancelled",
];

interface DeployActionsProps {
  screen: DeployScreen;
  deployId?: number;
}

export default function DeployActions({ screen, deployId }: DeployActionsProps) {
  const canCancel = screen === "in-progress";
  const isTerminal = TERMINAL_SCREENS.includes(screen);
  const isFailed = screen === "deploy-error";

  const { trigger: handleRetry, isLoading: retrying } = useRedeploy();
  const { mutate: cancelDeploy, isPending: isCancelling } = useCancelDeploy();
  const hasChanges = useHasDeployChanges();

  const handleCancel = useCallback(() => {
    if (!deployId) return;
    cancelDeploy(deployId);
  }, [deployId, cancelDeploy]);

  if (!canCancel && !isTerminal) return null;

  return (
    <div className="flex items-center gap-2">
      {canCancel && (
        <Button
          variant="outline"
          onClick={handleCancel}
          disabled={isCancelling || !deployId}
        >
          {isCancelling ? "Cancelling..." : "Cancel Deploy"}
        </Button>
      )}

      {isFailed && (
        <Button onClick={handleRetry} disabled={retrying}>
          {retrying ? "Retrying..." : "Retry Deploy"}
        </Button>
      )}

      {isTerminal && (
        <NewDeployButton onRedeploy={handleRetry} isLoading={retrying} hasChanges={hasChanges} />
      )}

      {isTerminal && <FullResetButton />}
    </div>
  );
}

function NewDeployButton({
  onRedeploy,
  isLoading,
  hasChanges,
}: {
  onRedeploy: () => void;
  isLoading: boolean;
  hasChanges: boolean | null;
}) {
  const handleClick = useCallback(() => {
    if (!confirm("Start a new deployment?")) return;
    onRedeploy();
  }, [onRedeploy]);

  if (!hasChanges) {
    return null;
  }

  const button = (
    <Button onClick={handleClick} disabled={isLoading}>
      {isLoading ? "Starting..." : hasChanges === null ? "Checking..." : "Redeploy"}
    </Button>
  );

  return button;
}
